import { useEffect, useMemo, useState } from "react";
import Card from "components/card";
import Loader from "components/loader/Loader";
import { LazyLoadImage } from "react-lazy-load-image-component";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import PaginationControls from "components/paginationControls/PaginationControls";
import { Box, Icon, Text, IconButton, Tooltip } from "@chakra-ui/react";
import { FiInbox } from "react-icons/fi";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { FaEye } from "react-icons/fa6";
import TooltipComponent from "components/tooltip";
import { MdBlock } from "react-icons/md";
import moment from "moment";
import 'moment/locale/es';
import { currencyFormat } from "utils/Funciones";

const columnHelper = createColumnHelper();

export default function TablePagination({
  searchTerm,
  tableData,
  fetchData,
  loading,
  pageCount,
  totalRow
}) {
  const [sorting, setSorting] = useState([]);

  const columns = useMemo(() => [
    columnHelper.accessor("imagen", {
      header: () => <span className="font-bold">Imagen</span>,
      cell: info => (
        <LazyLoadImage
          alt="Sin imagen"
          src={`/images/productos/${info.getValue()}`}
          effect="blur"
          className="w-12 h-12 object-cover rounded-lg"
        />
      ),
      enableSorting: false
    }),
    columnHelper.accessor("nombre", {
      header: () => <span className="font-bold">Nombre</span>,
      cell: info => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor("codigointerno", {
      header: () => <span className="font-bold">Código interno</span>,
      cell: info => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor("precioCompra", {
      header: () => <span className="font-bold">Precio compra promedio</span>,
      cell: info => <span className="font-semibold">{currencyFormat(info.getValue())}</span>,
    }),
    columnHelper.accessor("precioVentaPromedio", {
      header: () => <span className="font-bold">Precio venta promedio</span>,
      cell: info => <span className="font-semibold">{currencyFormat(info.getValue())}</span>,
    }),
    columnHelper.accessor("cantidadVendida", {
      header: () => <span className="font-bold">Cantidad vendida</span>,
      cell: info => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor("gananciaTotal", {
      header: () => <span className="font-bold">Ganancia total</span>,
      cell: info => <span className="font-semibold">{currencyFormat(info.getValue())}</span>,
    }),
  ], []);

  const table = useReactTable({
    data: tableData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount,
    debugTable: false,
  });

  useEffect(() => {
    const { pageIndex, pageSize } = table.getState().pagination ?? { pageIndex: 0, pageSize: 10 };
    fetchData(pageSize, pageIndex, searchTerm, sorting);
  }, [fetchData, searchTerm, sorting, table.getState().pagination]);

  return (
    <Card extra="w-full h-full overflow-auto p-4">
      <div className="overflow-x-auto">
        <Box className="w-full overflow-x-auto rounded-md">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-300/50 dark:bg-navy-600/50">
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className={`p-2 cursor-pointer font-semibold ${header.column.columnDef.meta?.headerClass || ''}`}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="border-t border-gray-300/50 dark:border-navy-600/50 hover:bg-gray-300/30 
                  dark:hover:bg-navy-600/30 transition-colors duration-500 ease-in-out">
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className={`p-2 ${cell.column.columnDef.meta?.headerClass || ''}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={table.getAllColumns().length}>
                    <Box
                      py={10}
                      textAlign="center"
                      className="text-center flex flex-col items-center justify-center text-gray-500 dark:text-gray-400"
                    >
                      <Icon as={FiInbox} boxSize={10} className="mb-2" />
                      <Text fontSize="md" fontWeight="medium">
                        No se encontraron resultados
                      </Text>
                    </Box>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>
        {loading && <Loader color="#64707d" className="mt-4" />}
      </div>

      <PaginationControls table={table} totalRow={totalRow} />

    </Card>
  );
}
